import { z } from "zod";
import { money, trimmed, uuid } from "./common";

/**
 * Акции (`addon.promotions_pro`, ТЗ 09 §9.4, BR-152…160): процент / сумма на заказ, категории или позиции, комбо по спеццене,
 * «купи X — получи Y» (BR-158), подарок от суммы; условия — дни и часы (happy hours, BR-157), даты, филиалы, тип заказа,
 * первый заказ, тег клиента, промокод, лимиты использований. Совместимость: приоритет → эксклюзивная (только она) →
 * в группе совместимости — самая выгодная, группы складываются; ручная скидка — поверх (BR-153).
 */
export const PROMO_TYPES = ["percent", "fixed", "combo", "bogo", "gift"] as const;
export type PromoType = (typeof PROMO_TYPES)[number];
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, { error: "Время ЧЧ:ММ" });
const isoDate = z.iso.date({ error: "Дата в формате ГГГГ-ММ-ДД" });

export const promotionSchema = z
  .strictObject({
    name: trimmed(2, 120),
    type: z.enum(PROMO_TYPES),
    isActive: z.boolean().default(true),
    /** Больше — раньше (при равной выгоде) */
    priority: z.number().int().min(0).max(1000).default(0),
    /** Эксклюзивная: если применима — только она */
    exclusive: z.boolean().default(false),
    /** Группа совместимости: из одной группы — одна (самая выгодная); без группы — складывается со всеми */
    stackGroup: trimmed(1, 40).nullable().default(null),
    /** Промокод: акция работает, только если код введён */
    code: z.string().trim().toUpperCase().regex(/^[A-Z0-9_-]{3,20}$/, { error: "Код: 3–20 латинских букв и цифр" }).nullable().default(null),
    /** На что действует: весь заказ, категории (с вложенными) или позиции */
    target: z.strictObject({ scope: z.enum(["order", "categories", "items"]), ids: z.array(uuid).max(200).default([]) }).default({ scope: "order", ids: [] }),
    percent: z.number().min(0.01).max(100).optional(),
    amount: money.optional(),
    /** Комбо: слоты (в каждом — подходящие позиции) и цена набора */
    comboSlots: z.array(z.array(uuid).min(1).max(50)).max(10).default([]),
    comboPrice: money.optional(),
    /** «Купи X — получи Y»: из группы X+Y самые дешёвые Y — со скидкой getPercent (100 — бесплатно) */
    buyQty: z.number().int().min(1).max(50).optional(),
    getQty: z.number().int().min(1).max(50).optional(),
    getPercent: z.number().min(1).max(100).default(100),
    giftItemId: uuid.nullable().optional(),
    conditions: z.strictObject({
      minSubtotal: money.default(0),
      days: z.array(z.number().int().min(1).max(7)).max(7).default([]),
      timeFrom: hhmm.nullable().default(null),
      timeTo: hhmm.nullable().default(null),
      dateFrom: isoDate.nullable().default(null),
      dateTo: isoDate.nullable().default(null),
      branchIds: z.array(uuid).max(100).default([]),
      orderTypes: z.array(z.enum(["dine_in", "takeaway", "delivery", "self_service"])).max(4).default([]),
      firstOrder: z.boolean().default(false),
      customerTag: trimmed(1, 30).nullable().default(null),
      usageLimit: z.number().int().min(1).nullable().default(null),
      perCustomerLimit: z.number().int().min(1).nullable().default(null),
    }).prefault({}),
  })
  .refine((p) => p.type !== "percent" || p.percent !== undefined, { error: "Укажите процент", path: ["percent"] })
  .refine((p) => p.type !== "fixed" || p.amount !== undefined, { error: "Укажите сумму", path: ["amount"] })
  .refine((p) => p.type !== "combo" || (p.comboSlots.length >= 2 && p.comboPrice !== undefined), { error: "Комбо: минимум 2 слота и цена", path: ["comboSlots"] })
  .refine((p) => p.type !== "bogo" || (p.buyQty !== undefined && p.getQty !== undefined), { error: "Укажите «купи» и «получи»", path: ["buyQty"] })
  .refine((p) => p.type !== "gift" || !!p.giftItemId, { error: "Выберите подарок", path: ["giftItemId"] })
  .refine((p) => p.target.scope === "order" || p.target.ids.length > 0, { error: "Выберите категории или позиции", path: ["target"] });
export type PromotionInput = z.input<typeof promotionSchema>;
export type Promotion = z.output<typeof promotionSchema> & { id: string };
/** Применить акции к заказу: коды и включение (выключить — снять все акции). */
export const orderPromotionsSchema = z.strictObject({ enabled: z.boolean(), codes: z.array(z.string().trim().toUpperCase().max(20)).max(5).default([]) });

export type PromoLine = { menuItemId: string; categoryIds: string[]; unitPrice: number; qty: number; total: number; allowDiscount: boolean };
export type PromoContext = {
  isoDow: number; hm: string; date: string; branchId: string; orderType: string; codes: string[];
  customerTags: string[]; firstOrder: boolean;
  /** Сколько раз уже использована (всего / этим клиентом), без текущего заказа */
  used: Record<string, { total: number; customer: number }>;
};
export type PromoResult = { promotionId: string; name: string; amount: number };

function inWindow(p: Promotion, c: PromoContext) {
  const k = p.conditions;
  if (k.days.length && !k.days.includes(c.isoDow)) return false;
  if (k.timeFrom && k.timeTo) {
    const ok = k.timeFrom <= k.timeTo ? c.hm >= k.timeFrom && c.hm < k.timeTo : c.hm >= k.timeFrom || c.hm < k.timeTo;
    if (!ok) return false;
  }
  if (k.dateFrom && c.date < k.dateFrom) return false;
  if (k.dateTo && c.date > k.dateTo) return false;
  if (k.branchIds.length && !k.branchIds.includes(c.branchId)) return false;
  if (k.orderTypes.length && !k.orderTypes.includes(c.orderType as never)) return false;
  if (k.firstOrder && !c.firstOrder) return false;
  if (k.customerTag && !c.customerTags.includes(k.customerTag)) return false;
  if (p.code && !c.codes.includes(p.code)) return false;
  const u = c.used[p.id] ?? { total: 0, customer: 0 };
  if (k.usageLimit !== null && u.total >= k.usageLimit) return false;
  if (k.perCustomerLimit !== null && u.customer >= k.perCustomerLimit) return false;
  return true;
}

const targeted = (p: Promotion, l: PromoLine) =>
  l.allowDiscount && (p.target.scope === "order" || (p.target.scope === "items" ? p.target.ids.includes(l.menuItemId) : l.categoryIds.some((c) => p.target.ids.includes(c))));

/** Единицы позиций (целые штуки) по убыванию цены — для «X+Y» и комбо. */
function units(lines: PromoLine[]) {
  return lines.flatMap((l) => Array.from({ length: Math.floor(l.qty + 1e-9) }, () => ({ menuItemId: l.menuItemId, price: l.unitPrice }))).sort((a, b) => b.price - a.price);
}

/** Выгода одной акции на заказ (minor), 0 — не подходит. */
export function promotionAmount(p: Promotion, lines: PromoLine[]): number {
  const own = lines.filter((l) => targeted(p, l));
  const base = own.reduce((s, l) => s + l.total, 0);
  const subtotal = lines.reduce((s, l) => s + l.total, 0);
  if (subtotal < p.conditions.minSubtotal) return 0;
  switch (p.type) {
    case "percent": return Math.round((base * (p.percent ?? 0)) / 100);
    case "fixed": return base > 0 ? Math.min(p.amount ?? 0, base) : 0;
    case "bogo": {
      const u = units(own);
      const size = (p.buyQty ?? 1) + (p.getQty ?? 1);
      let off = 0;
      for (let i = 0; i + size <= u.length; i += size) for (const x of u.slice(i + size - (p.getQty ?? 1), i + size)) off += (x.price * p.getPercent) / 100;
      return Math.round(off);
    }
    case "combo": {
      const pool = units(lines.filter((l) => l.allowDiscount));
      let off = 0;
      for (;;) {
        const picked: number[] = [];
        for (const slot of p.comboSlots) {
          const i = pool.findIndex((x, k) => slot.includes(x.menuItemId) && !picked.includes(k));
          if (i < 0) return Math.max(0, Math.round(off));
          picked.push(i);
        }
        const sum = picked.reduce((s, i) => s + pool[i]!.price, 0);
        off += Math.max(0, sum - (p.comboPrice ?? 0));
        for (const i of [...picked].sort((a, b) => b - a)) pool.splice(i, 1);
      }
    }
    case "gift": {
      const g = lines.find((l) => l.menuItemId === p.giftItemId && l.qty >= 1);
      return g ? g.unitPrice : 0;
    }
  }
}

/**
 * Какие акции применить (ТЗ 09 §9.4): подходящие по условиям → эксклюзивная (самая выгодная, при равенстве — приоритет) —
 * только она; иначе в каждой группе совместимости — самая выгодная, без группы — все; сумма не больше подытога.
 */
export function evaluatePromotions(promos: Promotion[], lines: PromoLine[], c: PromoContext): { applied: PromoResult[]; total: number } {
  const subtotal = lines.reduce((s, l) => s + l.total, 0);
  const cands = promos
    .filter((p) => p.isActive && inWindow(p, c))
    .map((p) => ({ p, amount: promotionAmount(p, lines) }))
    .filter((x) => x.amount > 0)
    .sort((a, b) => b.amount - a.amount || b.p.priority - a.p.priority);
  const exclusive = cands.find((x) => x.p.exclusive);
  let chosen: typeof cands;
  if (exclusive) chosen = [exclusive];
  else {
    const seen = new Set<string>();
    chosen = cands.filter((x) => {
      if (!x.p.stackGroup) return true;
      if (seen.has(x.p.stackGroup)) return false;
      seen.add(x.p.stackGroup);
      return true;
    });
  }
  let left = subtotal;
  const applied: PromoResult[] = [];
  for (const x of chosen.sort((a, b) => b.p.priority - a.p.priority || b.amount - a.amount)) {
    const amount = Math.min(x.amount, left);
    if (amount <= 0) break;
    applied.push({ promotionId: x.p.id, name: x.p.name, amount });
    left -= amount;
  }
  return { applied, total: subtotal - left };
}
